import { useEffect, useState } from "react";
import { fmtDate } from "@/lib/status";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { usePractice } from "@/pages/StaffApp";
import SignaturePad from "@/components/SignaturePad";
import SetPinDialog from "@/components/SetPinDialog";

const ID_METHODS = [["photo_id", "Government photo ID checked"], ["insurance_card_and_dob", "Insurance card + date of birth"],
  ["two_identifiers", "Two patient identifiers confirmed"], ["known_to_staff", "Known to staff (established patient)"]];

function Kiosk({ p, patient, close }) {
  const { me, reloadMe } = usePractice();
  const nav = useNavigate();
  const [method, setMethod] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const start = async () => {
    try {
      const { data } = await api.post(`/packets/${p.id}/signing-session`, { identity_method: method });
      nav(`/kiosk/${data.token}`);
    } catch (e) {
      if (e.response?.status === 428) setPinOpen(true); else toast.error(errMsg(e));
    }
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle>Start in-person signing</DialogTitle>
        <DialogDescription>Confirm you are handing the device to <b>{patient.first_name} {patient.last_name}</b> (DOB {fmtDate(patient.dob)}){p.signer.type === "representative" && <> - signer: <b>{p.signer.name}</b>, {p.signer.relationship}</>}. Staff mode locks until you re-enter your PIN.</DialogDescription>
      </DialogHeader>
      <Label>How did you verify identity?</Label>
      <RadioGroup value={method} onValueChange={setMethod} className="gap-2" data-testid="identity-method-group">
        {ID_METHODS.map(([v, l]) => (
          <label key={v} className="flex items-center gap-3 border rounded-md p-3 cursor-pointer hover:bg-slate-50">
            <RadioGroupItem value={v} data-testid={`identity-method-${v}`} />{l}
          </label>
        ))}
      </RadioGroup>
      {!me.has_pin && <p className="text-sm text-amber-700">You need a staff PIN before handing over the device.</p>}
      <DialogFooter>
        <Button variant="outline" onClick={close}>Cancel</Button>
        <Button className="btn-brand" disabled={!method} onClick={me.has_pin ? start : () => setPinOpen(true)} data-testid="kiosk-start-btn">
          {me.has_pin ? "Lock staff mode & hand over" : "Set PIN first"}
        </Button>
      </DialogFooter>
      <SetPinDialog open={pinOpen} onOpenChange={setPinOpen} onDone={reloadMe} />
    </>
  );
}

function Remote({ p, patient, remote, done }) {
  const [link, setLink] = useState(remote ? `${window.location.origin}/r/${remote.token}` : "");
  const [sms, setSms] = useState(false);
  useEffect(() => { api.get("/messaging/status").then((r) => setSms(r.data.sms)).catch(() => {}); }, []);
  const canText = patient.phone && patient.sms_opt_in;
  const text = async () => {
    try { await api.post(`/packets/${p.id}/remote-link/text`); toast.success("Text message sent"); done(false); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = async () => {
    try {
      const { data } = await api.post(`/packets/${p.id}/remote-link`);
      setLink(`${window.location.origin}/r/${data.token}`);
      done(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const email = async () => {
    try { await api.post(`/packets/${p.id}/remote-link/email`); toast.success("PHI-free invitation emailed"); done(false); } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async () => {
    try { await api.post(`/packets/${p.id}/remote-link/revoke`); setLink(""); toast.success("Link revoked"); done(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <DialogHeader>
        <DialogTitle>Remote signing link</DialogTitle>
        <DialogDescription>Short-lived (72h), revocable link. The signer must pass a last name + date of birth challenge; a forwarded link alone grants nothing.</DialogDescription>
      </DialogHeader>
      {link ? (
        <div className="space-y-3">
          <div className="flex gap-2"><Input readOnly value={link} className="font-mono text-xs" data-testid="remote-link-value" />
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }} data-testid="remote-link-copy"><Copy className="w-4 h-4" /></Button></div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={email} disabled={!patient.email} data-testid="remote-link-email-btn"><Mail className="w-4 h-4 mr-1" />{patient.email ? "Email invitation" : "No email on file"}</Button>
            <Button variant="outline" onClick={text} disabled={!canText || !sms} data-testid="remote-link-text-btn"><MessageSquare className="w-4 h-4 mr-1" />{!patient.phone ? "No cell number" : !patient.sms_opt_in ? "Texts not allowed" : sms ? "Text link" : "Texting not set up"}</Button>
            <Button variant="outline" onClick={create} data-testid="remote-link-resend-btn">Issue new link</Button>
            <Button variant="destructive" onClick={revoke} data-testid="remote-link-revoke-btn">Revoke</Button>
          </div>
          <p className="text-xs text-slate-500">Email and text contain no patient details - only a link and the organization-neutral sender name.</p>
          <p className="text-xs text-slate-600 bg-slate-50 border rounded p-2" data-testid="remote-reminder-note">{patient.email || (sms && canText)
            ? `Daily reminders go out automatically by ${[patient.email && "email", sms && canText && "text"].filter(Boolean).join(" and ")} in the patient's language until signed or the link expires.`
            : "No automatic reminders: no email on file and texting is not available for this patient."}</p>
        </div>
      ) : <Button className="btn-brand" onClick={create} data-testid="remote-link-create-btn">Create link</Button>}
    </>
  );
}

function Authority({ p, done }) {
  const [ref, setRef] = useState("");
  const decide = async (decision) => {
    try { await api.post(`/packets/${p.id}/authority-review`, { decision, evidence_ref: ref }); toast.success(`Authority ${decision}d`); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Review representative authority</DialogTitle>
        <DialogDescription>{p.signer.name} ({p.signer.relationship}) claims <b>{p.signer.authority_type?.replace(/_/g, " ")}</b> authority, verified by <b>{p.signer.verification_method}</b>. "Family member" alone is not sufficient.</DialogDescription></DialogHeader>
      <Label htmlFor="evref">Evidence reviewed (document type / reference)</Label>
      <Input id="evref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. Court guardianship order #GO-2291" data-testid="authority-evidence-input" />
      <DialogFooter>
        <Button variant="destructive" onClick={() => decide("reject")} data-testid="authority-reject-btn">Reject authority</Button>
        <Button className="btn-brand" onClick={() => decide("approve")} disabled={!ref} data-testid="authority-approve-btn">Approve authority</Button>
      </DialogFooter>
    </>
  );
}

function Witness({ p, done }) {
  const [sig, setSig] = useState(null);
  const [intent, setIntent] = useState(false);
  const save = async () => {
    try { await api.post(`/packets/${p.id}/witness`, { signature: sig, intent }); toast.success("Witness signature recorded"); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Provider / witness signature</DialogTitle><DialogDescription>Signing as yourself, not as the patient.</DialogDescription></DialogHeader>
      <SignaturePad onChange={setSig} testid="witness-pad" />
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={intent} onCheckedChange={(v) => setIntent(v === true)} data-testid="witness-intent-checkbox" />I witnessed this signing and intend to sign electronically.</label>
      <DialogFooter><Button className="btn-brand" disabled={!sig || !intent} onClick={save} data-testid="witness-submit-btn">Sign as witness</Button></DialogFooter>
    </>
  );
}

const OUTCOMES = [["refused", "Patient refused to sign"], ["unable_to_sign", "Unable to sign"], ["missing_representative", "Representative not present"],
  ["deferred", "Deferred for later review"], ["technical_failure", "Technical failure"]];

function Outcome({ p, done }) {
  const [oc, setOc] = useState("");
  const [reason, setReason] = useState("");
  const save = async () => {
    try { await api.post(`/packets/${p.id}/action`, { action: "outcome", outcome: oc, reason }); toast.success("Outcome recorded"); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Record exception</DialogTitle><DialogDescription>An exception is not completed consent. Care decisions stay with clinical operations.</DialogDescription></DialogHeader>
      <RadioGroup value={oc} onValueChange={setOc} className="gap-2">
        {OUTCOMES.map(([v, l]) => <label key={v} className="flex items-center gap-3 border rounded-md p-3 cursor-pointer"><RadioGroupItem value={v} data-testid={`outcome-${v}`} />{l}</label>)}
      </RadioGroup>
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (no clinical details)" data-testid="outcome-reason-input" />
      <DialogFooter><Button className="btn-brand" disabled={!oc} onClick={save} data-testid="outcome-submit-btn">Record outcome</Button></DialogFooter>
    </>
  );
}

function Ack({ p, done }) {
  const [ok, setOk] = useState(false);
  const save = async () => {
    try { await api.post(`/packets/${p.id}/export/ack`, { confirmed_chart_match: ok }); toast.success("Chart filing acknowledged - packet completed"); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Acknowledge chart filing</DialogTitle><DialogDescription>Folder delivery is not chart attachment. Open the EHR and confirm the document is attached to EHR chart <b>{p.ehr_id}</b>.</DialogDescription></DialogHeader>
      <label className="flex items-start gap-2 text-sm"><Checkbox checked={ok} onCheckedChange={(v) => setOk(v === true)} data-testid="ack-confirm-checkbox" />I verified the signed document is filed in the correct patient chart.</label>
      <DialogFooter><Button className="btn-brand" disabled={!ok} onClick={save} data-testid="ack-submit-btn">Mark completed</Button></DialogFooter>
    </>
  );
}

function Void({ p, done }) {
  const [reason, setReason] = useState("");
  const save = async () => {
    try { await api.post(`/packets/${p.id}/action`, { action: "void", reason }); toast.success("Packet voided"); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Void packet</DialogTitle><DialogDescription>Voids unsigned work and revokes any open signing links. History is kept.</DialogDescription></DialogHeader>
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" data-testid="void-reason-input" />
      <DialogFooter><Button variant="destructive" disabled={!reason} onClick={save} data-testid="void-submit-btn">Void</Button></DialogFooter>
    </>
  );
}

const MAP = { kiosk: Kiosk, remote: Remote, authority: Authority, witness: Witness, outcome: Outcome, ack: Ack, void: Void };

export default function ActionDialog({ type, data, onClose, onDone }) {
  const C = MAP[type];
  return (
    <Dialog open={!!type} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={`action-dialog-${type}`}>
        {C && <C p={data.packet} patient={data.patient} remote={data.remote} close={onClose}
          done={(closeIt = true) => { onDone(); if (closeIt) onClose(); }} />}
      </DialogContent>
    </Dialog>
  );
}
